import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AgroDashboard.css';

import FarmerHero from './FarmerHero';

// const BASE = 'https://atrmart.in/api';
const BASE = 'https://agro.atrmart.in/api';


const formatCount = (num) => {
  if (num < 10) return num;
  const floored = Math.floor(num / 10) * 10;
  return `${floored}+`;
};


const DIVISIONS = [
  { 
    name: 'Agro Fresh Products', 
    path: '/division/agro_fresh', 
    icon: '🥦', 
    desc: 'Farm-fresh vegetables, fruits, and daily essentials delivered daily.' 
  },
  { 
    name: 'Agro Products', 
    path: '/division/agro_products', 
    icon: '🌾', 
    desc: 'High-quality seeds, fertilizers, and essential farming inputs.' 
  },
  { 
    name: 'Value Added Products', 
    path: '/division/value_added', 
    icon: '📦', 
    desc: 'Processed natural goods like oils, pickles, and specialty masalas.' 
  },
];

// ─────────────────────────────────────────────
// PRODUCT CARD COMPONENT
// ─────────────────────────────────────────────
export const DashboardProductCard = ({ 
  item, 
  navigate, 
  addToCart, 
  cart, 
  user, 
  updateQuantity, 
  toggleWishlist, 
  isInWishlist, 
  onLoginClick 
}) => {




  const variants = (item.variants && item.variants.length > 0) 
    ? item.variants 
    : [{ size: item.unit || '1 unit', price: item.price || 0, mrp: item.mrp || 0 }];
  
  const [selectedVariant, setSelectedVariant] = useState(variants[0]);

  // Find if this specific product/variant is in the cart
  const cartItem = cart?.find(c => 
    (c.id === item.id || c.product_id === item.id) && 
    c.selectedWeight === selectedVariant.size
  );
  const quantityInCart = cartItem ? cartItem.quantity : 0;

  const handleAdd = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    // Check for any existing token key
    const token = localStorage.getItem('user_token') || localStorage.getItem('token');

    if (!token || !user) {
      onLoginClick(); 
      return;
    }

    // 1. Instant UI Update (Keep Frontend Snappy)
    addToCart({
      ...item,
      selectedWeight: selectedVariant.size,
      price: selectedVariant.price
    });

    // 2. Database Sync
    try {
      await axios.post(`${BASE}/cart/add`, {
        product_id: item.id,
        quantity: 1,
        variant: selectedVariant.size
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
    } catch (err) {
      console.error("Backend Sync Failed:", err);
      // We don't alert here to keep the "Instant Add" flow smooth
    }
  };

  const imgUrl = (img) => img 
    ? `https://atrmart.in/storage/${img}` 
    : 'https://via.placeholder.com/300x200?text=No+Image';

  return (
    <div className="product-card" onClick={() => navigate(`/product/${item.id}`, { state: { selectedWeight: selectedVariant.size } })}>
      <div className="card-image-zone">
        <img src={imgUrl(item.image)} alt={item.name} className="card-main-img" />
        <button 
          className={`wishlist-float ${isInWishlist ? 'loved' : ''}`} 
          onClick={e => { e.stopPropagation(); toggleWishlist(item); }}
        >
          {isInWishlist ? '♥' : '♡'}
        </button>
      </div>

      <div className="card-info">
        <h3>{item.name}</h3>
        <div className="variant-pills">
          {variants.map((v, idx) => (
            <button 
              key={idx} 
              className={`variant-pill ${selectedVariant.size === v.size ? 'active' : ''}`} 
              onClick={(e) => { e.stopPropagation(); setSelectedVariant(v); }}
            >
              {v.size}
            </button>
          ))}
        </div>

        <div className="price-action-row">
          <div className="price-group">
            <span className="main-price">₹{selectedVariant.price}</span>
            {selectedVariant.mrp > selectedVariant.price && (
              <span className="card-mrp">₹{selectedVariant.mrp}</span>
            )}
          </div>

          {quantityInCart === 0 ? (
            <button className="add-btn" onClick={handleAdd}>+ Add</button>
          ) : (
            <div className="qty-pill" onClick={e => e.stopPropagation()}>
              <button onClick={() => updateQuantity(item.id, selectedVariant.size, quantityInCart - 1)}>−</button>
              <span>{quantityInCart}</span>
              <button onClick={() => updateQuantity(item.id, selectedVariant.size, quantityInCart + 1)}>+</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// COUNTDOWN HOOK
// ─────────────────────────────────────────────
const useCountdown = (seconds) => {
  const [time, setTime] = useState(seconds);
  useEffect(() => {
    const iv = setInterval(() => setTime(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(iv);
  }, []);
  return {
    h: String(Math.floor(time / 3600)).padStart(2, '0'),
    m: String(Math.floor((time % 3600) / 60)).padStart(2, '0'),
    s: String(time % 60).padStart(2, '0'),
  };
};

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────
const AgroDashboard = ({ 
  user, 
  onLoginClick, 
  onLogout, 
  cart, 
  updateQuantity, 
  addToCart, 
  toggleWishlist, 
  wishlist = [],
  products: globalProducts = [], 
  categories: globalCategories = [] 
}) => {   
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // States
  const [sellerCount, setSellerCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([{ name: 'All', icon: '🏡', color: '#2d6a4f' }]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('');

  const { h, m, s } = useCountdown(4 * 3600 + 22 * 60 + 10);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetching Logic
const fetchInitialData = useCallback(async () => {
  setLoading(true);
  try {
    const [pRes, cRes, sRes] = await Promise.allSettled([
      axios.get(`${BASE}/products`),
      axios.get(`${BASE}/categories`),
      axios.get(`${BASE}/stats/sellers`)
    ]);

    // Log responses to debug if data is null/undefined
    console.log("API Responses:", { pRes, cRes, sRes });

    // Handle Products
    if (pRes.status === 'fulfilled') {
      const pData = pRes.value.data?.data || pRes.value.data || [];
      setProducts(pData);
    }

    // Handle Categories
    if (cRes.status === 'fulfilled') {
      const cData = cRes.value.data?.data || cRes.value.data || [];
      setCategories([
        { name: 'All', icon: '🏡', color: '#2d6a4f' },
        ...cData
      ]);
    }

    // Handle Seller Count
    if (sRes.status === 'fulfilled') {

      const data = sRes.value.data;
  // Log this to see exactly what the server sends
  console.log("Raw Seller Data:", data);

  
      // Adjust based on your API response structure (e.g., sRes.value.data.count)
      setSellerCount(sRes.value.data?.count || sRes.value.data || 0);
    }
  } catch (err) {
    console.error('❌ Data fetch critical error:', err);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Filtering Logic
  useEffect(() => {
    let temp = [...products];

    if (search) {
      const query = search.toLowerCase();
      temp = temp.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }

    if (category !== 'All') {
      temp = temp.filter(p => 
        p.category?.toLowerCase().trim() === category.toLowerCase().trim()
      );
    }

    if (sort === 'low-high') temp.sort((a, b) => a.price - b.price);
    else if (sort === 'high-low') temp.sort((a, b) => b.price - a.price);
    else if (sort === 'discount') {
      temp.sort((a, b) => {
        const dA = a.mrp ? (a.mrp - a.price) / a.mrp : 0;
        const dB = b.mrp ? (b.mrp - b.price) / b.mrp : 0;
        return dB - dA;
      });
    }

    setFilteredProducts(temp);
  }, [search, category, sort, products]);

  const flashDeals = products.filter(p => p.mrp && p.mrp > p.price).slice(0, 4);

  return (
    <div className="mart-root full-width-layout">
      <main className="mart-main">
        <header className="mart-topbar topbar-black">
          <div className="topbar-left-zone">
            <div className="logo-text-large" onClick={() => navigate('/agro')}>
              ATR AGRO MART
            </div>
            <nav className="top-nav-links">
              <button onClick={() => navigate('/agro')}>Home</button>
              <button onClick={() => navigate('/categories')}>Categories</button>
              <button onClick={() => navigate('/seller')}>Seller</button>
              <button onClick={() => navigate('/offers')}>Offers</button>
             {/* <button onClick={() => navigate('/reseller')}>Reseller</button>*/}
              <button onClick={() => navigate('/settings')}>Settings</button>

              <button onClick={() => navigate('/hub/dashboard')}>Hub Portal</button>
            </nav>
          </div>

          <div className="search-wrap-full">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search products, seeds, fertilizers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}           
            />
          </div>

          <div className="topbar-right">
            <button className="icon-btn" onClick={() => user ? navigate('/wishlist') : onLoginClick()}>♡</button>
            <button className="icon-btn cart-btn" onClick={() => user ? navigate('/cart') : onLoginClick()}>
              🛒
              {cart.length > 0 && <span className="cart-count-badge">{cart.length}</span>}
            </button>

            {user ? (
              <div className="user-menu-wrapper" ref={dropdownRef}>
                <div className="user-pill" onClick={() => setShowDropdown(!showDropdown)}>
                  <div className="user-avatar">{user.name ? user.name.charAt(0).toUpperCase() : 'U'}</div>
                  <span className="user-name-text">Hi, {user.name.split(' ')[0]}</span>
                  <span className="dropdown-arrow">{showDropdown ? '▴' : '▾'}</span>
                </div>
                {showDropdown && (
                  <div className="user-dropdown">
                    <button className="dropdown-item" onClick={() => navigate('/orders')}>📦 Orders</button>
                    <button className="dropdown-item" onClick={() => navigate('/profile')}>👤 Profile</button>
                    <button className="dropdown-item" onClick={() => navigate('/address')}>📍 Address</button>
                    <button className="dropdown-item" onClick={() => navigate('/track-order')}>🚚 Track Order</button>
                    <button className="dropdown-item logout-item" onClick={onLogout}>🚪 Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <button className="login-btn-premium" onClick={onLoginClick}>Login</button>
            )}
          </div>
        </header>

        <div className="mart-scroll">
          <FarmerHero onShopNow={() => document.getElementById('product-section').scrollIntoView({ behavior: 'smooth' })} />

          <div className="stats-ticker">
            {['🥦 100% Organic', '🚚 Fast Delivery', '🔄 Easy Returns', '⭐ Top Rated', '🌱 100% Natural', '📦 Same-day Packing', '🔒 Secure Payment'].map((item, i) => (
              <span key={i} className="ticker-item">{item}</span>
            ))}
          </div>

<section className="navigation-section">
  {/* Division Highlights */}
  <h2 className="section-title" style={{ fontSize: '1.2rem', marginBottom: '15px' }}>Shop by Division</h2>
  <div className="division-grid">
    {DIVISIONS.map((div, i) => (
      <div key={i} className="division-card" onClick={() => navigate(div.path)}>
        <div style={{ fontSize: '24px' }}>{div.icon}</div>
        {/* Name is now bold */}
        <h4 style={{ fontWeight: '800', margin: '10px 0', color: '#2d6a4f' }}>{div.name}</h4>
        <p>{div.desc}</p>
      </div>
    ))}
  </div>

  {/* Browse Categories */}
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
    <h2 className="section-title" style={{ fontSize: '1.2rem', margin: 0 }}>Browse Categories</h2>
    {/* Orange color changed to a theme-matching green */}
    <button 
      className="view-all-btn" 
      onClick={() => navigate('/categories')}
      style={{ 
        backgroundColor: '#2d6a4f', 
        color: 'white', 
        padding: '8px 16px', 
        borderRadius: '6px', 
        border: 'none', 
        cursor: 'pointer' 
      }}
    >
      View All →
    </button>
  </div>
  
  <div className="category-filter-strip">
    {categories.slice(0, 10).map((cat, i) => (
      <div key={i} className="cat-item" onClick={() => setCategory(cat.name)}>
        <span style={{ fontSize: '22px' }}>{cat.icon}</span>
        {/* Category names also made bold */}
        <span style={{ fontWeight: '700' }}>{cat.name}</span>
      </div>
    ))}
    
    <div className="cat-item view-more-card" onClick={() => navigate('/categories')}>
      <span style={{ fontSize: '22px' }}>➕</span>
      <span style={{ fontWeight: '700' }}>More</span>
    </div>
  </div>
</section>

          {/* Promo Section (Unchanged Flow) */}
          <section className="promo-section">
            <div className="promo-main">
              <div className="promo-leaf promo-leaf-1">🍃</div>
              <div className="promo-leaf promo-leaf-2">🌿</div>
              <div className="promo-content">
                <div className="promo-eyebrow">Seasonal Offer • 2026</div>
                <h2 className="promo-title">Fresh Organic Harvest</h2>
                <p className="promo-desc">Up to 30% off on your first order.<br />Use code <strong>FRESH30</strong></p>
                <button className="promo-btn" onClick={() => navigate('/categories')}>Explore Now →</button>
              </div>
              <div className="promo-visual">
                <div className="promo-circle c1" /><div className="promo-circle c2" /><div className="promo-circle c3" />
                <span className="promo-big-emoji">🥬</span>
              </div>
            </div>
            <div className="promo-secondary-stack">
              <div className="promo-refer" onClick={() => navigate('/refer')}>
                <span className="refer-icon">💸</span>
                <div><h4>Refer & Earn</h4><p>Get Upto <strong>₹100</strong> on  referral</p></div>
                <button className="refer-cta">Get Code →</button>
              </div>
<div className="promo-seller" onClick={() => navigate('/seller')}>
  <span className="seller-icon">🧑‍🌾</span>
  <div>
    <h4>Sell on ATR</h4>
    <p>Join {sellerCount > 100 ? `${Math.floor(sellerCount/100)*100}+` : sellerCount} verified farmers</p>
  </div>
  <button className="seller-cta">Join Now →</button>
</div>
            </div>
          </section>

         {/*  {flashDeals.length > 0 && (
            <section className="flash-section">
              <div className="flash-header">
                <div className="flash-title-group">
                  <span className="flash-icon">⚡</span>
                  <h2 className="flash-title">Flash Deals</h2>
                </div>
                <div className="flash-timer">
                  <span className="timer-label">Ends in</span>
                  <div className="timer-blocks">
                    <div className="timer-block"><span>{h}</span><label>HRS</label></div>
                    <span className="timer-sep">:</span>
                    <div className="timer-block"><span>{m}</span><label>MIN</label></div>
                    <span className="timer-sep">:</span>
                    <div className="timer-block"><span>{s}</span><label>SEC</label></div>
                  </div>
                </div>
              </div>
              <div className="flash-grid">
                {flashDeals.map(p => {
                  const disc = Math.round(((p.mrp - p.price) / p.mrp) * 100);
                  return (
                    <div key={p.id} className="flash-card" onClick={() => navigate(`/product/${p.id}`, { state: { selectedWeight: p.unit || '1kg' } })}>
                      <div className="flash-badge">{disc}% OFF</div>
                      <img src={p.image ? `https://atrmart.in/storage/${p.image}` : 'https://via.placeholder.com/150'} alt={p.name} />
                      <div className="flash-info">
                        <span className="flash-name">{p.name}</span>
                        <div className="flash-prices">
                          <span className="flash-price">₹{p.price}</span>
                          <span className="flash-mrp">₹{p.mrp}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )} */}

          <section className="products-section" id="product-section">
            <div className="products-header">
              <div>
                <h2 className="section-title">All Products</h2>
                <span className="product-count">{filteredProducts.length} items found</span>
              </div>
              <div className="sort-control">
                <select value={sort} onChange={e => setSort(e.target.value)}>
                  <option value="">Sort By</option>
                  <option value="low-high">Price: Low → High</option>
                  <option value="high-low">Price: High → Low</option>
                  <option value="discount">Best Discount</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton-card"><div className="skeleton-img" /><div className="skeleton-line long" /></div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🌾</span>
                <h3>No products found</h3>
                <button onClick={() => { setSearch(''); setCategory('All'); setSort(''); }}>Clear Filters</button>
              </div>
            ) : (
              <div className="product-grid">
                {filteredProducts.map(item => (
                  <DashboardProductCard
                    key={item.id}
                    item={item}
                    cart={cart}
                    updateQuantity={updateQuantity}
                    navigate={navigate}
                    addToCart={addToCart}
                    toggleWishlist={toggleWishlist}
                    user={user}
                    onLoginClick={onLoginClick}
                    isInWishlist={wishlist.some(w => w.id === item.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Professional Footer */}
          <footer className="atr-footer-pro">
            <div className="footer-newsletter">
              <div className="footer-container">
                <div className="newsletter-text">
                  <h3>Join the ATR Farming Community</h3>
                  <p>Get weekly updates on market prices and seasonal offers.</p>
                </div>
                <div className="newsletter-form">
                  <input type="email" placeholder="Enter your email address" />
                  <button>Subscribe Now</button>
                </div>
              </div>
            </div>

            <div className="footer-main-content">
              <div className="footer-container">
                <div className="footer-column brand-info">
                  <div className="footer-logo-wrap"><span className="logo-icon">🌿</span><h2 className="footer-logo">ATR AGRO MART</h2></div>
                  <p className="footer-desc">Empowering South Indian agriculture through technology. From Madurai to the world.</p>
                  <div className="contact-card">
                    <div className="c-icon">📞</div>
                    <div className="c-details"><span>Customer Care</span><a href="tel:+919344453444">+91 93444 53444</a></div>
                  </div>
                </div>

                <div className="footer-column">
                  <h4>Explore</h4>
                  <ul className="footer-links-list">
                    <li onClick={() => navigate('/agro')}>Marketplace</li>
                    <li onClick={() => navigate('/seller')}>Become a Seller</li>
                    <li onClick={() => navigate('/about')}>About ATR Group</li>
                  </ul>
                </div>

                <div className="footer-column">
  <h4>Trust Center</h4>
  <ul className="footer-links-list">
    <li onClick={() => navigate('/privacypolicy')}>Privacy Policy</li>
   <li onClick={() => navigate('/terms-conditions')}>Terms & Conditions</li>
<li onClick={() => navigate('/refundpolicy')}>Refund Policy</li>
<li onClick={() => navigate('/shippingpolicy')}>Shipping Policy</li>  
</ul>
</div>

                <div className="footer-column">
                  <h4>Corporate Office</h4>
                  <p>ATR MART,<br />12A, Vaigai Nagar 2nd Street, Madurai, 625017</p>
                </div>
              </div>
            </div>

            <div className="footer-bottom-bar">
              <div className="footer-container">
                <div className="copy-text">© 2026 ATR AGRO MART. ATR Group.</div>
                <div className="payment-icons">UPI • VISA • COD</div>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default AgroDashboard;